import React, { useState, useEffect, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import Select from 'v3/components/Select';
import Input from 'v3/components/Input';
import InputAddress from 'v3/components/InputAddress';
import Button from 'v3/components/Button';
import Text from 'v3/components/Text';
import { fetchSelectOptions } from 'v3/pages/Travel/Register/controller';
import api from 'services/api';
import { DELIVERY_TYPE_OPTIONS } from 'v3/utils/constants';
import moment from 'moment';
import { validateAddressAll, validateSequentialDates } from '../Validation';
import {
  extractCountryNameFromAddress,
  findCountryOption,
  hasCountryValue,
} from '../utils/country';

export default function AddAddress({
  data = {},
  onChange = () => {},
  onSave = () => {},
  countryOptions = [],
  showTypeSelect = false,
  action,
  isFirst = false,
  snackbar = null,
  origins = [],
  destinations = [],
  pointsListRef,
  disabled = false,
  validateRetroactive = true,
}) {
  const [showDetails, setShowDetails] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [disableNumber,       setDisableNumber]       = useState(false);
  const [disableNeighborhood, setDisableNeighborhood] = useState(false);
  const [disableZipCode, setDisableZipCode] = useState(false);
  const [disableCity, setDisableCity] = useState(false);
  const [disableProvince, setDisableProvince] = useState(false);
  const inputRef = useRef();

  function disableAddress(address = {}) {
    setDisableNumber(      !!address.number);
    setDisableNeighborhood(!!address.neighborhood);
    setDisableZipCode(!!address.zip_code);
    setDisableCity(!!address?.city);
    setDisableProvince(!!address?.city?.province?.uf || !!address?.province || !!data?.province);
  }

  const handleChange = (field, value) => {
    onChange((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  function setDestinationCity(city, { action }) {
    if (action === 'create-option') {
      onChange((prev) => ({
        ...prev,
        city: {
          name: city?.id,
        },
        province: '',
      }));
      return;
    }

    if (action === 'clear') {
      onChange((prev) => ({
        ...prev,
        city: null,
        province: '',
      }));
      return;
    }

    onChange((prev) => ({
      ...prev,
      city,
      province: city?.province?.uf || '',
    }));
  }

  async function fetchCities(search, uf, country) {
    try {
      const response = await api.get('cities', {
        params: { search, uf, country },
      });
      return response.data;
    } catch (ex) {
      return [];
    }
  }

  function requiredLabel(label) {
    return (
      <>
        {' '}
        {label} <span style={{ color: 'red' }}> *</span>
      </>
    );
  }

  const handleFullAddressChange = (value) => {
    if (!value) {
      onChange((prev) => ({
        ...prev,
        client: null,
        clientId: null,
        address: '',
        number: '',
        complement: '',
        zip_code: '',
        neighborhood: '',
        city: null,
        cityId: null,
        province: '',
        lat: null,
        lng: null,
        formatted_address: '',
      }));
      return;
    }

    const customer = {
      ...value,
      cgccpf: value.cgccpf || '',
      social_name: value.social_name || value.label?.split(' - ')[0] || '',
    };

    const address = customer?.addresses?.[0];
    const country = findCountryOption(countryOptions, {
      abbreviation: address?.country?.abbreviation,
      name: address?.country?.name,
    });
    const brazil = findCountryOption(countryOptions, { abbreviation: 'br', name: 'Brasil' });

    const filledFields = {
      client: customer,
      clientId: customer?.id || null,
      address: address?.address || '',
      number: address?.number || '',
      complement: address?.complement || '',
      zip_code: address?.zip_code || '',
      neighborhood: address?.neighborhood || '',
      city: address?.city,
      cityId: address?.city?.id || null,
      province: address?.city?.province?.uf || '',
      lat: address?.lat,
      lng: address?.lng,
      formatted_address: address?.formatted_address || '',
      country: country || brazil,
    };

    onChange((prev) => ({ ...prev, ...filledFields }));
    disableAddress({
      number: address.number,
      complement: address.complement,
      neighborhood: address.neighborhood,
      zip_code: address.zip_code,
      city: address.city,
    });
  };

  useEffect(() => {
    if (action === 'edit') {
      disableAddress({
        number: data.number,
        complement: data.complement,
        neighborhood: data.neighborhood,
        zip_code: data.zip_code,
        city: data.city,
      });
    }
  }, [action]);

  useEffect(() => {
    if (hasCountryValue(data?.country)) {
      return;
    }

    const matchedCountry =
      findCountryOption(countryOptions, {
        name: extractCountryNameFromAddress(data?.formatted_address || data?.address),
      }) ||
      findCountryOption(countryOptions, {
        abbreviation: data?.country?.abbreviation,
        name: data?.country?.name,
      }) ||
      null;

    if (matchedCountry) {
      handleChange('country', matchedCountry);
    }
  }, [countryOptions, data?.country, data?.formatted_address, data?.address]);

  useEffect(() => {
    setErrors(data?.validationErrors || {});
  }, [data?.validationErrors]);

  useEffect(() => {
    if (data?.type) return;

    const defaultType = isFirst
      ? DELIVERY_TYPE_OPTIONS.find((type) => type.id === 'collect')
      : showTypeSelect
        ? DELIVERY_TYPE_OPTIONS.find((type) => type.id === 'delivery')
        : null;

    if (defaultType) {
      handleChange('type', defaultType);
    }
  }, [isFirst, showTypeSelect, data?.type]);

  const handleSelectedAddress = async (place) => {
    const [city] = await fetchCities(place.city, place.province, place.country_abbreviation);
    const selectedCountry =
      findCountryOption(countryOptions, {
        abbreviation: place.country_abbreviation,
        name: place.country,
      }) ||
      findCountryOption(countryOptions, {
        name: extractCountryNameFromAddress(place?.formatted_address || place?.address),
      }) ||
      (hasCountryValue(data?.country) ? data.country : null);

    const full = {
      address: place?.address || '',
      number: place?.number || '',
      complement: place?.name || '',
      zip_code: place?.zip_code || '',
      neighborhood: place?.neighborhood || '',
      city: city || null,
      province: city?.province?.uf || '',
      lat: place?.lat,
      lng: place?.lng,
      formatted_address: place?.formatted_address,
      country: selectedCountry,
    };

    onChange((prev) => ({ ...prev, ...full }));
    disableAddress({
      number: place.number,
      complement: place.name,
      neighborhood: place.neighborhood,
      zip_code: place.zip_code,
      city: city || null,
    });
  };

  function getPointLetter(point, list) {
    const index = list.findIndex((p) => p.identifier === point.identifier);
    if (index < 0) return '';
    const baseCharCode = 'A'.charCodeAt(0);
    return String.fromCharCode(baseCharCode + index);
  }

  function getPointIndex(point, list) {
    const index = list.findIndex((p) => p.identifier === point.identifier);
    return index >= 0 ? index + 1 : null;
  }

  function getTypeLabel(type) {
    const id = typeof type === 'string' ? type : type?.id;
    switch (id) {
      case 'collect':
        return 'COLETA';
      case 'customhouse':
        return 'ADUANA';
      case 'delivery':
        return 'ENTREGA';
      default:
        return 'PONTO';
    }
  }

  async function onSubmit(e) {
    e?.preventDefault?.();
    if (disabled || isSubmitting) return;

    setIsSubmitting(true);
    try {
    const result = await validateAddressAll(data, {
      isFirst,
      origins,
      destinations,
      validateRetroactive,
    });

    if (!result.isValid) {
      setErrors(result.errors);
      if (snackbar) {
        snackbar.show(<Text>{Object.values(result.errors)[0]}</Text>, { type: 'error' });
      }
      return;
    }

    const fullList = [...origins, ...destinations];
    const allPoints = [...fullList];
    const currentIndex = fullList.findIndex((p) => p.identifier === data.identifier);

    if (action === 'edit' && currentIndex >= 0) {
      allPoints[currentIndex] = data;
    } else {
      allPoints.push(data);
    }

    const sequentialErrors = validateSequentialDates(allPoints);

    if (sequentialErrors.length > 0) {
      const errorIdx = sequentialErrors[0];
      const current = allPoints[errorIdx];
      const previous = allPoints[errorIdx - 1];

      const msg = `A data de início do ponto #${
        errorIdx + 1
      } ${current?.type?.name?.toUpperCase()} (${moment(current.start_schedule).format(
        'DD/MM/YYYY HH:mm',
      )}) não pode ser menor que a data máxima do ponto #${errorIdx} ${previous?.type?.name?.toUpperCase()} (${moment(
        previous.scheduled_time,
      ).format('DD/MM/YYYY HH:mm')}).`;

      setErrors({ start_schedule: msg });
      if (snackbar) snackbar.show(<Text>{msg}</Text>, { type: 'error' });
      return;
    }

    setErrors({});
    await onSave(
      {
        ...data,
        identifier: data.identifier || new Date().getTime(),
        type: isFirst ? DELIVERY_TYPE_OPTIONS.find((type) => type.id === 'collect') : data.type,
      },
      action,
    );

    if (pointsListRef?.current) {
      pointsListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (data) {
      setShowDetails(true);
    }
  }, [data]);

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center mt-4 mb-3"
        onClick={() => setShowDetails((prev) => !prev)}
      >
        <Text type="label">{showDetails ? '▼ Ocultar Detalhes' : '▶ Mostrar Detalhes'}</Text>
        <i
          className={`fa ${showDetails ? 'fa-chevron-up' : 'fa-chevron-down'}`}
          style={{ color: '#0056b3', cursor: 'pointer' }}
        />
      </div>

      {showDetails && (
        <div style={{ padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: '#e8f1ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 8,
                }}
              >
                <i className="fa fa-map-marker-alt" style={{ color: '#0056b3', fontSize: 14 }} />
              </div>
              <Text type="header" className="m-0" color="dark">
                {(() => {
                  const fullList = [...origins, ...destinations];
                  const pointIndex = getPointIndex(data, fullList);
                  const typeLabel = getTypeLabel(data?.type);

                  if (isFirst) return 'Cadastro da primeira coleta';

                  if (pointIndex) {
                    return `Edição do #${pointIndex} ${typeLabel}`;
                  }

                  return 'Cadastro de um novo destino';
                })()}
              </Text>
            </div>
            {!isFirst && showTypeSelect && (
              <div style={{ width: 200 }}>
                <Select
                  label="Tipo"
                  isDisabled={disabled}
                  options={DELIVERY_TYPE_OPTIONS}
                  value={data?.type}
                  onChange={(opt) => handleChange('type', opt)}
                  getOptionLabel={(o) => o.name}
                  getOptionValue={(o) => o.id}
                  error={errors.type}
                />
              </div>
            )}
          </div>

          <hr style={{ borderTop: '1px solid #dee2e6', marginBottom: '1.5rem' }} />

          <Row className="mb-3">
            <Col md={6}>
              <Select.Async
                label="Cliente Tomador"
                isDisabled={disabled}
                onSearch={(search) => fetchSelectOptions('persons/customers', { search })}
                value={data?.client}
                getOptionLabel={(option) =>
                  option?.social_name
                    ? `${option.social_name} - ${option.cgccpf}`
                    : option?.label || ''
                }
                getOptionValue={(option) => option?.id}
                onChange={(event) => handleFullAddressChange(event)}
                id="select-modal-endereco-cliente"
              />
            </Col>
            <Col md={6}>
              <Select
                label={requiredLabel('País')}
                isDisabled={disabled}
                options={countryOptions}
                value={data.country}
                onChange={(event) => handleChange('country', event)}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                error={errors.country}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={8}>
              <InputAddress
                ref={inputRef}
                disabled={disabled}
                label={requiredLabel('Logradouro')}
                value={data.address || ''}
                onChange={(e) => handleChange('address', e.target.value)}
                onSelectedAddress={handleSelectedAddress}
                country={data.country?.abbreviation || 'br'}
                error={errors.address}
              />
            </Col>
            <Col md={4}>
              <Input
                label="Número"
                disabled={disabled || disableNumber}
                value={data.number || ''}
                onChange={(e) => handleChange('number', e.target.value)}
                error={errors.number}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Input
                label="Complemento"
                disabled={disabled}
                value={data.complement || ''}
                onChange={(e) => handleChange('complement', e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Input
                label="Bairro"
                disabled={disabled || disableNeighborhood}
                value={data.neighborhood || ''}
                onChange={(e) => handleChange('neighborhood', e.target.value)}
              />
            </Col>
            <Col md={4}>
              <Input
                label="CEP"
                disabled={disabled || disableZipCode}
                value={data.zip_code || ''}
                onChange={(e) => handleChange('zip_code', e.target.value)}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Select.Async
                label={requiredLabel('Cidade')}
                isDisabled={disabled || disableCity}
                onSearch={(city) => fetchCities(city, '', data?.country?.abbreviation || 'br')}
                value={data.city || ''}
                horizontal
                creatable
                onChange={setDestinationCity}
                getOptionLabel={(option) =>
                  `${`${option.name} ${option.province?.uf ? `- ${option.province?.uf}` : ''}`}`
                }
                getOptionValue={(option) =>
                  `${`${option.name} ${option.province?.uf ? `- ${option.province?.uf}` : ''}`}`
                }
                error={errors.city}
                id="select-cidade"
              />
            </Col>
            <Col md={6}>
              <Input
                label="UF"
                disabled={disabled || disableProvince}
                value={data.province || ''}
                onChange={(e) => handleChange('province', e.target.value)}
                error={errors.province}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Input
                label={requiredLabel('Latitude')}
                disabled
                value={data.lat || ''}
                error={errors.lat}
              />
            </Col>
            <Col md={6}>
              <Input
                label={requiredLabel('Longitude')}
                disabled
                value={data.lng || ''}
                error={errors.lng}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Input
                label={requiredLabel('Data Início')}
                disabled={disabled}
                type="datetime-local"
                value={data.start_schedule}
                onChange={(e) => handleChange('start_schedule', e.target.value)}
                error={errors.start_schedule}
              />
            </Col>
            <Col md={6}>
              <Input
                label={requiredLabel('Data Máxima')}
                disabled={disabled}
                type="datetime-local"
                value={data.scheduled_time}
                onChange={(e) => handleChange('scheduled_time', e.target.value)}
                error={errors.scheduled_time}
              />
            </Col>
          </Row>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="primary"
              loading={isSubmitting}
              disabled={disabled || isSubmitting}
              onClick={onSubmit}
            >
              {action === 'add' ? 'Adicionar novo destino' : 'Editar destino'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
